import { useState } from 'react'
import {
  Stack, Text, Group, Box, Select, Button, Alert, Textarea, Divider,
} from '@mantine/core'
import { IconCheck, IconX, IconAlertCircle } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useQuery } from '@tanstack/react-query'
import { registrationApi } from '@/api/registration'
import { rolesApi } from '@/api/roles'
import { permissionsApi } from '@/api/permissions'
import type { UserRow } from './UsersTable'

interface UserDrawerPendingApprovalProps {
  user: UserRow
  onClose: () => void
  onActionComplete: () => void
}

function formatDate(iso?: string | null): string {
  if (!iso) return '\u2014'
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Text size="xs" c="var(--ai-text-muted)" fw={500}>{label}</Text>
      <Text size="sm">{value || '\u2014'}</Text>
    </Box>
  )
}

export function UserDrawerPendingApproval({ user, onClose, onActionComplete }: UserDrawerPendingApprovalProps) {
  const [roleCode, setRoleCode] = useState<string | null>(null)
  const [templateId, setTemplateId] = useState<string | null>(null)
  const [approving, setApproving] = useState(false)
  const [showReject, setShowReject] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [rejecting, setRejecting] = useState(false)

  const regId = user.registration_id

  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.listRoles(),
  })
  const roleOptions = roles.map((r) => ({ value: r.code, label: r.name || r.code }))

  const { data: templates = [] } = useQuery({
    queryKey: ['permission-templates'],
    queryFn: () => permissionsApi.listTemplates(),
  })
  const templateOptions = templates.map((t) => ({ value: String(t.id), label: t.name }))

  const handleApprove = async () => {
    if (!regId || !roleCode || !templateId) return
    setApproving(true)
    try {
      const res = await registrationApi.approve(regId, roleCode, Number(templateId))
      notifications.show({
        color: 'green', title: 'Aprovado',
        message: 'Cadastro aprovado com sucesso!',
        icon: <IconCheck size={16} />,
      })
      if (res.notifications && !res.notifications.whatsapp) {
        notifications.show({
          color: 'yellow', title: 'Aviso',
          message: 'WhatsApp não foi enviado. O usuário foi notificado apenas por email.',
        })
      }
      onActionComplete()
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      notifications.show({ color: 'red', title: 'Erro', message: msg || 'Falha ao aprovar.' })
    } finally {
      setApproving(false)
    }
  }

  const handleReject = async () => {
    if (!regId || rejectReason.trim().length < 10) return
    setRejecting(true)
    try {
      await registrationApi.reject(regId, rejectReason.trim())
      notifications.show({
        color: 'orange', title: 'Rejeitado',
        message: 'Cadastro rejeitado. Usuário notificado.',
      })
      onActionComplete()
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      notifications.show({ color: 'red', title: 'Erro', message: msg || 'Falha ao rejeitar.' })
    } finally {
      setRejecting(false)
    }
  }

  return (
    <Stack gap="md" p="md" style={{ flex: 1, overflow: 'auto' }}>
      {/* Registration details */}
      <Text size="xs" fw={700} c="#098d93" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
        Dados do Cadastro
      </Text>
      <InfoField label="Nome completo" value={user.full_name} />
      <InfoField label="Email" value={user.email} />
      <InfoField label="Telefone" value={user.phone ?? '\u2014'} />
      <InfoField label="Endereço" value={user.address ?? '\u2014'} />
      <InfoField label="Organização" value={user.organization_name ?? '\u2014'} />
      <InfoField label="Matrícula" value={user.employee_number ?? '\u2014'} />
      <InfoField label="Data da solicitação" value={formatDate(user.created_at)} />

      <Divider my="xs" />

      {/* Approve section */}
      <Text size="xs" fw={700} c="#098d93" tt="uppercase" style={{ letterSpacing: '0.05em' }}>
        Aprovar Cadastro
      </Text>
      <Select
        label="Perfil (Role)"
        placeholder="Selecione o perfil..."
        data={roleOptions}
        value={roleCode}
        onChange={setRoleCode}
      />
      <Select
        label="Perfil de permissões"
        placeholder="Selecione o template..."
        data={templateOptions}
        value={templateId}
        onChange={setTemplateId}
        searchable
        nothingFoundMessage="Nenhum template encontrado"
        required
      />
      <Alert color="blue" radius="sm" variant="light">
        <Text size="xs">
          Ao aprovar, uma conta sera criada automaticamente e o usuário
          recebera a senha temporária por email e WhatsApp.
        </Text>
      </Alert>
      <Button
        color="green"
        leftSection={<IconCheck size={16} />}
        loading={approving}
        disabled={!roleCode || !templateId}
        onClick={handleApprove}
      >
        Aprovar
      </Button>

      <Divider my="xs" />

      {/* Reject section */}
      {!showReject ? (
        <Button
          color="red"
          variant="outline"
          leftSection={<IconX size={16} />}
          onClick={() => setShowReject(true)}
        >
          Rejeitar
        </Button>
      ) : (
        <Stack gap="sm">
          <Textarea
            label="Motivo da Rejeição"
            placeholder="Informe o motivo (mínimo 10 caracteres)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            minRows={3}
          />
          {rejectReason.length > 0 && rejectReason.trim().length < 10 && (
            <Alert icon={<IconAlertCircle size={16} />} color="orange" radius="sm" variant="light">
              <Text size="xs">O motivo deve ter pelo menos 10 caracteres.</Text>
            </Alert>
          )}
          <Group gap="sm">
            <Button
              color="red"
              loading={rejecting}
              disabled={rejectReason.trim().length < 10}
              onClick={handleReject}
            >
              Confirmar Rejeição
            </Button>
            <Button
              variant="subtle"
              color="gray"
              onClick={() => { setShowReject(false); setRejectReason('') }}
              disabled={rejecting}
            >
              Cancelar
            </Button>
          </Group>
        </Stack>
      )}
    </Stack>
  )
}
